import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  useController,
  Control,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import styles from "./SwitchText.module.scss";

// Props for React Hook Form mode
interface SwitchTextFormProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  disabled?: boolean;
  className?: string;
  value?: never;
  defaultValue?: never;
  onChange?: never;
}

// Props for standalone mode with local state
interface SwitchTextStandaloneProps {
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
  name?: never;
  control?: never;
}

type SwitchTextProps<T extends FieldValues> =
  | SwitchTextFormProps<T>
  | SwitchTextStandaloneProps;

// Internal component for rendering the switch UI
interface SwitchUIProps {
  isActive: boolean;
  onToggle: () => void;
  disabled: boolean;
  className: string;
}

const SwitchUI: React.FC<SwitchUIProps> = ({
  isActive,
  onToggle,
  disabled,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles.switchContainer} ${className}`}>
      <motion.div
        className={`${styles.switch} ${
          isActive ? styles.active : styles.inactive
        } ${disabled ? styles.disabled : ""}`}
        onClick={onToggle}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}>
        <motion.div
          className={styles.switchText}
          animate={{
            color: isActive ? "#ffffff" : "#000000",
            left: isActive ? "40%" : "60%",
            transform: isActive ? "translateX(-40%)" : "translateX(-60%)",
          }}
          transition={{ duration: 0.2 }}>
          {isActive ? t("active") : t("inactive")}
        </motion.div>

        <motion.div
          className={styles.switchToggle}
          animate={{
            x: isActive ? 0 : -45,
            backgroundColor: "#ffffff",
          }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
          }}
        />
      </motion.div>
    </div>
  );
};

// Form mode component
const SwitchTextForm = <T extends FieldValues>({
  name,
  control,
  disabled = false,
  className = "",
}: SwitchTextFormProps<T>) => {
  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
    defaultValue: false as T[FieldPath<T>],
  });

  const isActive = Boolean(value);

  const handleToggle = () => {
    if (!disabled) {
      onChange(!value);
    }
  };

  return (
    <SwitchUI
      isActive={isActive}
      onToggle={handleToggle}
      disabled={disabled}
      className={className}
    />
  );
};

// Standalone mode component
const SwitchTextStandalone: React.FC<SwitchTextStandaloneProps> = ({
  value,
  defaultValue = false,
  onChange,
  disabled = false,
  className = "",
}) => {
  const [localValue, setLocalValue] = useState(defaultValue);

  const currentValue = value !== undefined ? value : localValue;
  const isActive = Boolean(currentValue);

  const handleToggle = () => {
    if (!disabled) {
      const newValue = !currentValue;
      setLocalValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  };

  return (
    <SwitchUI
      isActive={isActive}
      onToggle={handleToggle}
      disabled={disabled}
      className={className}
    />
  );
};

// Main component that decides which mode to use
const SwitchText = <T extends FieldValues>(props: SwitchTextProps<T>) => {
  const isFormMode = "control" in props && props.control;

  if (isFormMode) {
    return <SwitchTextForm {...(props as SwitchTextFormProps<T>)} />;
  }

  return <SwitchTextStandalone {...(props as SwitchTextStandaloneProps)} />;
};

export default SwitchText;
